import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LifeBuoy, Send } from 'lucide-react';
import API from '@/lib/api';

const CATEGORIES = [
  { value: 'general', label: 'General enquiry' },
  { value: 'report_issue', label: 'Report an issue' },
  { value: 'payout_billing', label: 'Payout & billing' },
  { value: 'trust_safety', label: 'Trust & safety' },
  { value: 'campaign_support', label: 'Campaign support' },
  { value: 'other', label: 'Other' },
];

export default function SupportDialog({
  open,
  onOpenChange,
  defaultCategory = 'general',
  defaultSubject = '',
  context = null,
  title = 'Contact support',
  description = 'Send us a message and our team will reply within 24 hours on business days.',
}) {
  const [category, setCategory] = useState(defaultCategory);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCategory(defaultCategory);
      setSubject(defaultSubject);
      setMessage('');
    }
  }, [open, defaultCategory, defaultSubject]);

  const submit = async () => {
    if (subject.trim().length < 2) {
      toast.error('Please add a short subject.');
      return;
    }
    if (message.trim().length < 5) {
      toast.error('Please describe your issue in a bit more detail.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post('/support/ticket', {
        category,
        subject: subject.trim(),
        message: message.trim(),
        context: context || {},
      });
      toast.success(data.message || 'Message sent — we\'ll be in touch soon.');
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Could not send your message.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="support-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <LifeBuoy className="w-4 h-4 text-blue-600" />
            </div>
            <DialogTitle>{title}</DialogTitle>
          </div>
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs text-slate-600">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="support-category-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value} data-testid={`support-category-${c.value}`}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Subject</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Short summary"
              maxLength={200}
              className="mt-1"
              data-testid="support-subject-input"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-600">Message</Label>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Tell us what's happening, including any relevant campaign or deal names."
              rows={6}
              maxLength={5000}
              className="mt-1 resize-none"
              data-testid="support-message-textarea"
            />
            <p className="text-[10px] text-slate-400 mt-1">{message.length}/5000 characters</p>
          </div>

          {context && Object.keys(context).length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid="support-context-preview">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Included with your message</p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {Object.entries(context).map(([k, v]) => (
                  <span key={k} className="text-[11px] text-slate-500"><span className="text-slate-400">{k}:</span> {String(v)}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="support-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="support-submit-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {submitting ? 'Sending…' : 'Send message'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
